// src/rms/hooks/useElementHeightVar.js
// ============================================================================
// Publishes an element's measured height as a CSS custom property
// ============================================================================
// For the one case CSS cannot express on its own: something sticky that has to
// park directly underneath something else whose height is not knowable in
// advance.
//
// The app header is that case. The ad rail and the sidebar were pinned at a
// hardcoded 66px while the header stands 104.6px tall with a 728x90 masthead
// in it, so both locked ~39px too high and slid under it -- putting the top of
// the first rail ad behind the header. IntersectionObserver measures against
// the viewport and knows nothing about occlusion, so that ad still cleared the
// 50%-for-one-second threshold and still counted a viewable impression. We
// would have billed advertisers for pixels nobody saw.
//
// A bigger constant would have fixed today and broken at the next change to
// the bar, and could never be right at two breakpoints at once: below 1200px
// the masthead wraps to a full-width strip and the header grows.
//
// THREE SIGNALS, because no one of them catches every change:
//
//   ResizeObserver   layout changes -- the masthead wrapping, a font swapping
//   load (capture)   ad creatives arriving. The creatives are <img> and
//                    <iframe> that can land as late as useViewability's 1500ms
//                    render fallback, and `load` does not bubble, so it has to
//                    be caught on the way down.
//   settle passes    a few scheduled re-measures over the first ~2s
//   resize           the window itself
//
// The settle passes are not belt-and-braces. ResizeObserver is driven by the
// frame loop and does not fire at all in a non-compositing view -- a
// backgrounded tab, an embedded pane, a headless harness -- and `load` never
// fires for a creative that was already cached or never arrives. Both of the
// event-driven signals can therefore be silent on a page that is nonetheless
// laid out and on screen, which is exactly how the header ended up measured at
// 82px (no masthead yet) and left there while it actually stood at 104.6px.
// Timers are the one signal that works regardless, so they decide correctness
// and the observers decide how quickly it converges.
//
// The variable is set on the document root, not the element, so any descendant
// of any subtree can position against it.
// ============================================================================

import { useCallback, useEffect, useRef } from "react";

export default function useElementHeightVar(varName) {
  const nodeRef = useRef(null);
  const cleanupRef = useRef(null);

  const publish = useCallback(
    (node) => {
      if (!node || typeof document === "undefined") return;
      // Sub-pixel heights are real -- borders, line-height -- and rounding one
      // away is what leaves a one-pixel seam under a sticky bar.
      const { height } = node.getBoundingClientRect();
      if (height > 0) {
        document.documentElement.style.setProperty(varName, `${height}px`);
      }
    },
    [varName]
  );

  /**
   * A callback ref rather than an effect reading ref.current: the header
   * mounts and unmounts with the operator shell, and an effect would bind to a
   * stale node on the render where it swaps.
   *
   * All teardown lives here, in one place. Splitting it between here and an
   * effect cleanup is how the observer ended up disconnected but never rebuilt
   * under StrictMode's simulated remount -- which left the variable frozen at
   * whatever the header measured before its ads arrived.
   */
  const setRef = useCallback(
    (node) => {
      cleanupRef.current?.();
      cleanupRef.current = null;
      nodeRef.current = node;
      if (!node) return;

      publish(node);

      /**
       * Measure now, and again on the next tick.
       *
       * The second pass is not redundant: a `resize` handler runs before the
       * browser has necessarily reflowed everything the new width implies --
       * and the change that matters most here is exactly that kind, the
       * masthead wrapping to a full-width strip below 1200px and taking the
       * header from 105px to 262px. Reading synchronously can catch the old
       * height; reading again after the tick cannot.
       */
      let pending = null;
      const republish = () => {
        publish(node);
        clearTimeout(pending);
        pending = setTimeout(() => publish(node), 60);
      };

      let observer = null;
      if (typeof ResizeObserver !== "undefined") {
        observer = new ResizeObserver(republish);
        observer.observe(node);
      }

      // Capture phase: `load` does not bubble, so a listener on the header
      // never sees its own images finish without it.
      node.addEventListener("load", republish, true);
      node.addEventListener("error", republish, true);

      // Brackets the ad render fallback (1500ms) on both sides, then one last
      // look once everything has settled.
      const timers = [0, 250, 900, 1700, 2600].map((ms) => setTimeout(republish, ms));

      cleanupRef.current = () => {
        observer?.disconnect();
        clearTimeout(pending);
        timers.forEach(clearTimeout);
        node.removeEventListener("load", republish, true);
        node.removeEventListener("error", republish, true);
      };
    },
    [publish]
  );

  useEffect(() => {
    const onResize = () => publish(nodeRef.current);
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
      // Not the observer -- setRef owns that. See the note above.
      document.documentElement.style.removeProperty(varName);
    };
  }, [publish, varName]);

  return setRef;
}
